/**
 * Security Rate Limiter
 * Implements in-memory and storage-backed rate limiting to defend against
 * brute force attacks, credential stuffing, and telemetry flooding.
 */

interface RateLimitRecord {
  attempts: number;
  firstAttemptAt: number;
  lockedUntil?: number;
}

const memoryStore = new Map<string, RateLimitRecord>();

function getStorageKey(action: string, id: string): string {
  return `rate_limit_${action}_${id}`;
}

function getRecord(action: string, id: string): RateLimitRecord {
  const memKey = `${action}:${id}`;
  if (memoryStore.has(memKey)) {
    return memoryStore.get(memKey)!;
  }

  if (typeof window !== 'undefined') {
    try {
      const raw = sessionStorage.getItem(getStorageKey(action, id));
      if (raw) {
        const parsed = JSON.parse(raw);
        memoryStore.set(memKey, parsed);
        return parsed;
      }
    } catch {}
  }

  const fresh: RateLimitRecord = {
    attempts: 0,
    firstAttemptAt: Date.now(),
  };
  memoryStore.set(memKey, fresh);
  return fresh;
}

function saveRecord(action: string, id: string, record: RateLimitRecord): void {
  const memKey = `${action}:${id}`;
  memoryStore.set(memKey, record);
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.setItem(getStorageKey(action, id), JSON.stringify(record));
    } catch {}
  }
}

/**
 * Checks and increments rate limit for an action and identifier.
 * Returns whether the action is permitted and when to retry if locked.
 */
export function checkRateLimit(
  action: string,
  identifier: string,
  maxAttempts: number,
  windowMs: number,
  lockoutMs: number = 60000
): {
  allowed: boolean;
  remainingAttempts: number;
  retryAfterSeconds: number;
  error?: string;
} {
  const now = Date.now();
  const record = getRecord(action, identifier);

  // Check if currently locked out
  if (record.lockedUntil && now < record.lockedUntil) {
    const retryAfter = Math.ceil((record.lockedUntil - now) / 1000);
    return {
      allowed: false,
      remainingAttempts: 0,
      retryAfterSeconds: retryAfter,
      error: `Demasiados intentos fallidos. Bloqueado temporalmente por seguridad. Reintenta en ${retryAfter} segundos.`,
    };
  }

  // Reset window if expired
  if (now - record.firstAttemptAt > windowMs) {
    record.attempts = 0;
    record.firstAttemptAt = now;
    record.lockedUntil = undefined;
  }

  if (record.attempts >= maxAttempts) {
    record.lockedUntil = now + lockoutMs;
    saveRecord(action, identifier, record);
    const retryAfter = Math.ceil(lockoutMs / 1000);
    return {
      allowed: false,
      remainingAttempts: 0,
      retryAfterSeconds: retryAfter,
      error: `Demasiados intentos fallidos. Bloqueado temporalmente. Espera ${retryAfter} segundos.`,
    };
  }

  return {
    allowed: true,
    remainingAttempts: maxAttempts - record.attempts,
    retryAfterSeconds: 0,
  };
}

/**
 * Records a failed attempt for an action.
 */
export function recordRateLimitFailure(
  action: string,
  identifier: string,
  maxAttempts: number,
  lockoutMs: number = 60000
): void {
  const record = getRecord(action, identifier);
  record.attempts += 1;
  if (record.attempts >= maxAttempts) {
    record.lockedUntil = Date.now() + lockoutMs;
  }
  saveRecord(action, identifier, record);
}

/**
 * Resets the rate limit counter upon successful operation.
 */
export function resetRateLimit(action: string, identifier: string): void {
  const memKey = `${action}:${identifier}`;
  memoryStore.delete(memKey);
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(getStorageKey(action, identifier));
    } catch {}
  }
}

/**
 * Rate limit login attempts: max 5 attempts per 5 minutes; lockout for 2 minutes.
 */
export function checkLoginRateLimit(email: string) {
  return checkRateLimit('login', email.trim().toLowerCase(), 5, 5 * 60 * 1000, 2 * 60 * 1000);
}

export function recordLoginFailure(email: string) {
  recordRateLimitFailure('login', email.trim().toLowerCase(), 5, 2 * 60 * 1000);
}

export function resetLoginRateLimit(email: string) {
  resetRateLimit('login', email.trim().toLowerCase());
}

/**
 * Rate limit web request submissions: max 3 per 30 minutes.
 */
export function checkWebRequestRateLimit(identifier: string) {
  return checkRateLimit('web_request', identifier, 3, 30 * 60 * 1000, 15 * 60 * 1000);
}
